<template>
  <div>
    <!-- Mobile -->
    <div class="schedule-class-row-mobile px-2 py-4 d-flex d-md-none align-center"
      :class="{ 'schedule-class-row--last': isLast }">
      <v-checkbox v-if="selectable" :input-value="selected" class="mr-1 mt-0 pt-0 flex-shrink-0" hide-details
        @change="$emit('toggle-select', classData._id)" />
      <div class="schedule-class-row-indicator-mobile-wrap mr-2 flex-shrink-0 d-flex align-center justify-center"
        :style="{ backgroundColor: indicatorColor }">
        <v-avatar v-if="instructorImage" size="32">
          <ImageDisplay :src="instructorImage" :alt="instructorLine" style="object-fit: cover;" />
        </v-avatar>
      </div>
      <div class="flex-grow-1 min-width-0 pr-2">
        <div class="text-subtitle-2 font-weight-medium plusIndigoDarken1--text text-truncate">
          {{ title }}
        </div>
        <span class="text-caption">{{ dayText }} · {{ timeText }}</span>
      </div>
      <v-menu v-if="isSmallerDisplay()" offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" icon small class="flex-shrink-0">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <!-- Info section -->
          <v-list-item class="py-1" style="min-height: unset;">
            <div class="d-flex flex-column" style="gap: 6px; padding: 4px 0;">
              <div class="d-flex align-center">
                <v-icon size="14" color="plusSubtitle">mdi-clock-outline</v-icon>
                <span class="text-caption ml-1 plusTextSecondary--text">{{ timeText }}</span>
              </div>
              <div v-if="instructorLine && instructorLine !== '—'" class="d-flex align-center">
                <v-icon size="14" color="plusSubtitle">mdi-police-badge-outline</v-icon>
                <span class="text-caption ml-1 plusTextSecondary--text">{{ instructorLine }}</span>
              </div>
              <div v-if="classData.maxCapacity" class="d-flex align-center">
                <v-icon size="14" color="plusSubtitle">mdi-account-group-outline</v-icon>
                <span class="text-caption ml-1 plusTextSecondary--text">{{ $t('message.max_capacity') }}: {{
                  classData.maxCapacity }}</span>
              </div>
              <div class="d-flex align-center">
                <v-icon size="14" color="plusSubtitle">mdi-currency-usd-off</v-icon>
                <span class="text-caption ml-1 plusTextSecondary--text">{{ pricingLabel }}</span>
              </div>
              <div class="d-flex align-center">
                <v-icon size="14" color="plusSubtitle">mdi-emoticon-plus-outline</v-icon>
                <span class="text-caption ml-1 plusTextSecondary--text">{{ membershipLabel }}</span>
              </div>
              <div v-if="classData.maxTrialAttendance" class="d-flex align-center">
                <v-icon size="14" color="plusSubtitle">mdi-account-clock-outline</v-icon>
                <span class="text-caption ml-1 plusTextSecondary--text">{{ $t('message.max_trial_attendance') }}: {{ classData.maxTrialAttendance }}</span>
              </div>
              <div v-if="classData.disableSameDayTrial" class="d-flex align-center">
                <v-icon size="14" color="plusSubtitle">mdi-calendar-remove-outline</v-icon>
                <span class="text-caption ml-1 plusTextSecondary--text">{{ $t('message.disable_same_day_trial') }}</span>
              </div>
            </div>
          </v-list-item>
          <v-divider />
          <!-- Actions -->
          <v-list-item v-allowed="'edit_schedule'" @click="$emit('edit', classData)">
            <v-list-item-title>
              <v-icon small class="mr-1">mdi-square-edit-outline</v-icon>
              {{ $t('message.edit') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item v-allowed="'edit_schedule'" @click="$emit('delete', classData)">
            <v-list-item-title>
              <v-icon small class="mr-1">mdi-trash-can-outline</v-icon>
              {{ $t('message.delete') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <template v-else>
        <v-btn v-allowed="'edit_schedule'" icon small class="flex-shrink-0" @click="$emit('edit', classData)">
          <v-icon>mdi-square-edit-outline</v-icon>
        </v-btn>
        <v-btn v-allowed="'edit_schedule'" icon small class="flex-shrink-0" @click="$emit('delete', classData)">
          <v-icon>mdi-trash-can-outline</v-icon>
        </v-btn>
      </template>
    </div>

    <!-- Desktop -->
    <div class="schedule-class-row pt-4 pb-6 px-4 d-none d-md-flex align-stretch align-center"
      :class="{ 'schedule-class-row--last': isLast }">
      <v-checkbox v-if="selectable" :input-value="selected" class="mr-2 mt-0 pt-0 flex-shrink-0" hide-details
        @change="$emit('toggle-select', classData._id)" />

      <div class="schedule-class-row-thumbnail rounded-lg d-flex align-center justify-center flex-shrink-0"
        :style="thumbnailStyle">
        <v-avatar v-if="instructorImage" size="52">
          <ImageDisplay :src="instructorImage" :alt="instructorLine" style="object-fit: cover;" />
        </v-avatar>
        <v-icon v-else :color="thumbnailIconColor" size="32">mdi-karate</v-icon>
      </div>

      <div class="d-flex flex-column flex-grow-1 " style="gap: 8px;">
        <div class="font-weight-bold text-subtitle-1 plusIndigoDarken1--text">
          {{ title }}
        </div>

        <v-row class="d-flex flex-row mt-1">
          <v-col cols="4" class="d-flex py-1">
            <div>
              <div>
                <v-icon size="18" color="plusSubtitle">mdi-clock-outline</v-icon>
                <span class="text-body-1 ml-2 font-weight-regular">{{ timeText }}</span>
              </div>

              <div>
                <v-icon size="18" color="plusSubtitle">mdi-police-badge-outline</v-icon>
                <span class="text-body-1 ml-2 font-weight-regular">{{ `Instructor: ${instructorLine}` }}</span>
              </div>

              <div v-if="locationText">
                <v-icon size="18" color="plusSubtitle">mdi-map-marker-outline</v-icon>
                <span class="text-body-1 ml-2 font-weight-regular">{{ locationText }}</span>
              </div>

              <!-- <div>
                <v-icon size="18" color="plusSubtitle">mdi-calendar-month-outline</v-icon>
                <span class="text-body-1 ml-2 font-weight-regular">{{ dayText }}</span>
              </div> -->

            </div>
          </v-col>

          <v-col cols="4" class="d-flex py-1">
            <div class="d-flex flex-column pl-2 schedule-class-row-col-divider" style="gap: 4px;">
              <div>
                <v-icon size="18" color="plusSubtitle">mdi-currency-usd-off</v-icon>
                <span class="text-body-1 ml-2 font-weight-regular">{{ `${pricingLabel} class` }}</span>
              </div>
              <div class="d-flex align-start">
                <v-icon size="18" color="plusSubtitle" class="flex-shrink-0">mdi-information-outline</v-icon>
                <span class="text-body-1 ml-2 font-weight-regular">{{ participantsText }}</span>
              </div>
            </div>
          </v-col>

          <v-col cols="4" class="d-flex py-1">
            <div class="d-flex flex-column pl-2 justify-center schedule-class-row-col-divider" style="gap: 4px;">

              <div class="d-flex align-start">
                <v-icon size="18" color="plusSubtitle" class="flex-shrink-0">mdi-emoticon-plus-outline</v-icon>
                <span class="text-body-1 ml-2 font-weight-regular">{{ membershipLabel }}</span>
              </div>
              <div v-if="classData.maxTrialAttendance" class="d-flex align-start">
                <v-icon size="18" color="plusSubtitle" class="flex-shrink-0">mdi-account-clock-outline</v-icon>
                <span class="text-body-1 ml-2 font-weight-regular">{{ $t('message.max_trial_attendance') }}: {{ classData.maxTrialAttendance }}</span>
              </div>
              <div v-if="classData.disableSameDayTrial" class="d-flex align-start">
                <v-icon size="18" color="plusSubtitle" class="flex-shrink-0">mdi-calendar-remove-outline</v-icon>
                <span class="text-body-1 ml-2 font-weight-regular">{{ $t('message.disable_same_day_trial') }}</span>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <div class="d-flex align-start flex-shrink-0 pt-1">
        <v-btn v-allowed="'edit_schedule'" icon small @click="$emit('edit', classData)">
          <v-icon>mdi-square-edit-outline</v-icon>
        </v-btn>
        <v-btn v-allowed="'edit_schedule'" icon small @click="$emit('delete', classData)">
          <v-icon>mdi-trash-can-outline</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import ImageDisplay from "@/components/displays/ImageDisplay.vue";
export default {
  name: 'ScheduleClassRow',
  components: { ImageDisplay },
  props: {
    classData: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    dayText: {
      type: String,
      default: ''
    },
    timeText: {
      type: String,
      default: ''
    },
    instructorLine: {
      type: String,
      default: ''
    },
    instructorImage: {
      type: String,
      default: null
    },
    pricingLabel: {
      type: String,
      default: ''
    },
    participantsText: {
      type: String,
      default: ''
    },
    membershipLabel: {
      type: String,
      default: ''
    },
    locationText: {
      type: String,
      default: ''
    },
    isLast: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Boolean,
      default: false
    }
  },
  emits: ['edit', 'delete', 'toggle-select'],
  computed: {
    indicatorColor() {

      return this.classData.color || 'var(--v-plusSubtitle-base, #BDBDBD)';
    },
    thumbnailStyle() {
      if (this.classData.color) {
        return { backgroundColor: this.classData.color };
      }
      return { backgroundColor: 'var(--v-plusGreyLighten5-base, #F5F5F5)' };
    },
    thumbnailIconColor() {
      return this.classData.color ? 'white' : 'plusSubtitle';
    }
  }
};
</script>

<style lang="scss" scoped>
.schedule-class-row {
  align-items: stretch;
  gap: 16px;
  transition: background-color 0.15s;
  border-bottom: 1px solid var(--v-plusBorder-base, #e0e0e0);
}

.schedule-class-row:hover {
  background-color: var(--v-plusGreyLighten5-base, #fafafa);
}

.schedule-class-row--last {
  border-bottom: none;
}

.schedule-class-row-indicator {
  width: 6px;
  min-width: 6px;
  border-radius: 3px;
  align-self: stretch;
}

.schedule-class-row-indicator-mobile-wrap {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
}

.schedule-class-row-thumbnail {
  width: 76px;
  min-width: 76px;
  height: 76px;
  align-self: center;
}

.schedule-class-row-col-divider {
  border-left: 1px solid var(--v-plusBorder-base, #e0e0e0);
}

.min-width-0 {
  min-width: 0;
}

.schedule-class-row-mobile {
  align-items: center;
  gap: 8px;
  transition: background-color 0.15s;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);

  .theme--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }
}
</style>
